// components/CaseView/ReviewerAuthorityStrip.tsx
// Thin strip rendered inside CallTranscription card between status bar and transcript area.
// Prompts the physician to confirm whether the reviewer can overturn the denial.

export type ReviewerAuthority = "pending" | "yes" | "no" | "skipped";

interface ReviewerAuthorityStripProps {
  status: ReviewerAuthority;
  onAnswer: (answer: ReviewerAuthority) => void;
}

export function ReviewerAuthorityStrip({
  status,
  onAnswer,
}: ReviewerAuthorityStripProps) {
  // Dismissed — render nothing
  if (status === "skipped") return null;

  // Confirmed — compact teal badge
  if (status === "yes") {
    return (
      <div className="flex items-center justify-between px-3 py-1.5 -mx-4 bg-teal-500/8 border-y border-teal-500/15">
        <div className="flex items-center gap-2">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-teal-400"
          >
            <path d="M2 6.5l3 3 5-6" />
          </svg>
          <span className="text-[11px] font-medium text-teal-400">
            Decision-maker confirmed
          </span>
        </div>
        <button
          onClick={() => onAnswer("skipped")}
          className="text-slate-600 hover:text-slate-400 transition-colors p-0.5"
          aria-label="Dismiss"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M2 2l6 6M8 2l-6 6" />
          </svg>
        </button>
      </div>
    );
  }

  // Escalation coaching
  if (status === "no") {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2 -mx-4 bg-gradient-to-r from-amber-500/10 to-amber-500/5 border-y border-amber-500/20">
        <div className="w-4 h-4 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <svg
            width="8"
            height="8"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-amber-400"
          >
            <path d="M6 2v5M6 10v.01" />
          </svg>
        </div>
        <p className="text-xs text-slate-300 italic leading-snug flex-1 min-w-0">
          "I'd like to speak with the physician who has determination authority
          on this case, as I want to ensure we can resolve this today."
        </p>
        <button
          onClick={() => onAnswer("skipped")}
          className="text-slate-600 hover:text-slate-400 transition-colors flex-shrink-0 p-0.5"
          aria-label="Dismiss"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M2 2l6 6M8 2l-6 6" />
          </svg>
        </button>
      </div>
    );
  }

  // Pending — full Step 1 strip
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 -mx-4 bg-gradient-to-r from-amber-500/8 to-transparent border-y border-amber-500/15">
      {/* Step number */}
      <div className="w-4 h-4 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
        <span className="text-[9px] font-bold text-amber-400">1</span>
      </div>

      {/* Prompt text */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <span className="text-xs font-semibold text-slate-300">Ask:</span>
        <span className="text-xs text-slate-400 italic truncate">
          "Are you the physician making the determination?"
        </span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onAnswer("yes")}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold
            bg-teal-500/12 text-teal-300 border border-teal-500/30
            hover:bg-teal-500/20 hover:border-teal-500/50 active:scale-95
            transition-all duration-150"
        >
          <svg
            width="9"
            height="9"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M2 6.5l3 3 5-6" />
          </svg>
          Yes
        </button>
        <button
          onClick={() => onAnswer("no")}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold
            bg-amber-500/10 text-amber-300 border border-amber-500/25
            hover:bg-amber-500/18 hover:border-amber-500/40 active:scale-95
            transition-all duration-150"
        >
          <svg
            width="9"
            height="9"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M3 6h6M6 3l3 3-3 3" />
          </svg>
          Escalate
        </button>
        <button
          onClick={() => onAnswer("skipped")}
          className="px-1.5 py-1 text-slate-600 hover:text-slate-400 transition-colors"
          aria-label="Skip"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M2 2l6 6M8 2l-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}